import { useId } from 'react'

/**
 * Le portrait d'une bête de fin de saison, peint en couches SVG.
 *
 * Même grammaire que {@link CaptainPortrait}, à dessein : médaillon de rayon 53
 * centré (60,60), cadre de cordage doré au rayon 55,4, dégradés doux et lumière
 * venue du haut. Une chasse est le seul écran du jeu où l'adversaire a un visage,
 * et ce visage doit appartenir au même monde que celui du capitaine.
 *
 * Les identifiants de dégradés et de filtres sont préfixés par un `useId()` :
 * deux portraits sur la même page (le launcher et le tableau de chasse, par
 * exemple) ne doivent pas se voler leurs `url(#…)`.
 *
 * Clé inconnue (bundle plus vieux que le catalogue serveur) → on rend le Kraken
 * plutôt que de casser l'écran.
 */
export type BeastKey = 'kraken' | 'ghost_armada'

const LABELS: Record<BeastKey, string> = {
  kraken: 'Le Kraken',
  ghost_armada: "L'Armada fantôme",
}

export function BeastPortrait({
  archetypeKey,
  size = 160,
  className = '',
  title,
}: {
  archetypeKey: string
  size?: number
  className?: string
  /** Libellé accessible ; par défaut le nom de la bête. */
  title?: string
}) {
  const uid = useId().replace(/:/g, 'i')
  const key: BeastKey = archetypeKey === 'ghost_armada' ? 'ghost_armada' : 'kraken'

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={className}
      role="img"
      aria-label={title ?? LABELS[key]}
      data-beast={key}
    >
      {key === 'ghost_armada' ? <GhostArmada uid={uid} /> : <Kraken uid={uid} />}
    </svg>
  )
}

/**
 * Vu d'en dessous, dans la lumière de surface : deux longs tentacules ont déjà
 * pris les coques, le reste des bras monte. Peau mouchetée par une turbulence
 * fractale, œil de céphalopode (sphère sombre, anneau d'iris, pupille en fente).
 */
function Kraken({ uid }: { uid: string }) {
  return (
    <>
      <defs>
      <radialGradient id={`${uid}-k-water`} cx="0.5" cy="0.02" r="1.05">
      <stop offset="0" stopColor="#39A7BE"/><stop offset="0.22" stopColor="#12708C"/>
      <stop offset="0.55" stopColor="#083B4C"/><stop offset="1" stopColor="#010A0E"/>
      </radialGradient>
      <linearGradient id={`${uid}-k-ray`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#CFEDF3" stopOpacity="0.34"/><stop offset="1" stopColor="#CFEDF3" stopOpacity="0"/>
      </linearGradient>

      <linearGradient id={`${uid}-k-body`} x1="0.18" y1="0.05" x2="0.82" y2="1">
      <stop offset="0" stopColor="#C0703F"/><stop offset="0.28" stopColor="#8E4322"/>
      <stop offset="0.68" stopColor="#552312"/><stop offset="1" stopColor="#230C05"/>
      </linearGradient>
      <linearGradient id={`${uid}-k-arm`} x1="0.1" y1="0" x2="0.9" y2="1">
      <stop offset="0" stopColor="#A85B31"/><stop offset="0.55" stopColor="#6B2F16"/><stop offset="1" stopColor="#2A1007"/>
      </linearGradient>
      <linearGradient id={`${uid}-k-arm-deep`} x1="0.1" y1="0" x2="0.9" y2="1">
      <stop offset="0" stopColor="#6B3219"/><stop offset="1" stopColor="#1C0A04"/>
      </linearGradient>
      <linearGradient id={`${uid}-k-fin`} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor="#93482A" stopOpacity="0.95"/><stop offset="1" stopColor="#2A1007" stopOpacity="0.75"/>
      </linearGradient>

      <radialGradient id={`${uid}-k-iris`} cx="0.42" cy="0.34" r="0.75">
      <stop offset="0" stopColor="#FFF3CE"/><stop offset="0.3" stopColor="#F0BB56"/>
      <stop offset="0.72" stopColor="#B4761F"/><stop offset="1" stopColor="#4A2408"/>
      </radialGradient>
      <radialGradient id={`${uid}-k-eyeglow`} cx="0.5" cy="0.5" r="0.5">
      <stop offset="0" stopColor="#FFD98A" stopOpacity="0.42"/><stop offset="1" stopColor="#FFD98A" stopOpacity="0"/>
      </radialGradient>

      <linearGradient id={`${uid}-k-vign`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#010A0E" stopOpacity="0"/><stop offset="0.62" stopColor="#010A0E" stopOpacity="0.3"/><stop offset="1" stopColor="#010A0E" stopOpacity="0.72"/>
      </linearGradient>
      <linearGradient id={`${uid}-k-ring`} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor="#F0DCA8"/><stop offset="0.55" stopColor="#A5813F"/><stop offset="1" stopColor="#5A2A12"/>
      </linearGradient>

      {/* peau : chromatophores mouchetés */}
      <filter id={`${uid}-k-skin`} x="-20%" y="-20%" width="140%" height="140%">
      <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed="7" result="n"/>
      <feColorMatrix in="n" type="matrix"
      values="0 0 0 0 0.42  0 0 0 0 0.16  0 0 0 0 0.06  0 0 0 -1.1 0.62" result="mottle"/>
      <feComposite in="mottle" in2="SourceGraphic" operator="in"/>
      </filter>
      <filter id={`${uid}-k-soft`} x="-40%" y="-40%" width="180%" height="180%">
      <feGaussianBlur stdDeviation="1.6"/>
      </filter>
      <filter id={`${uid}-k-soft2`} x="-40%" y="-40%" width="180%" height="180%">
      <feGaussianBlur stdDeviation="0.7"/>
      </filter>

      <clipPath id={`${uid}-k-clip`}><circle cx="60" cy="60" r="53"/></clipPath>
      </defs>

      <g clipPath={`url(#${uid}-k-clip)`}>
      <rect x="7" y="7" width="106" height="106" fill={`url(#${uid}-k-water)`}/>

      {/* ── la surface, vue d'en dessous ─────────────────────────────────── */}
      <path d="M7 22 C 18 17.5, 27 24, 38 20.5 C 49 17, 58 23.5, 69 20 C 80 16.5, 90 23, 100 19.5 C 106 17.6, 110 18.6, 113 20 L 113 7 L 7 7 Z"
      fill="#7FCADC" opacity="0.22"/>
      <path d="M7 22 C 18 17.5, 27 24, 38 20.5 C 49 17, 58 23.5, 69 20 C 80 16.5, 90 23, 100 19.5 C 106 17.6, 110 18.6, 113 20"
      fill="none" stroke="#DDF5FA" strokeWidth="0.9" opacity="0.5"/>
      <g filter={`url(#${uid}-k-soft)`} opacity="0.55">
      <path d="M34 20 L46 20 L52 62 L28 62 Z" fill={`url(#${uid}-k-ray)`}/>
      <path d="M64 20 L78 20 L92 58 L70 58 Z" fill={`url(#${uid}-k-ray)`} opacity="0.8"/>
      <path d="M12 20 L20 20 L14 50 L4 50 Z" fill={`url(#${uid}-k-ray)`} opacity="0.5"/>
      </g>

      {/* ── deux galions en silhouette, tirés vers le fond ───────────────── */}
      <g transform="translate(34 24) rotate(-15)" fill="#04161D">
      <path d="M-11 0 L11 0 L8 5.6 L-8 5.6 Z"/>
      <path d="M-0.7 0 L-0.7 -12 L0.7 -12 L0.7 0 Z"/>
      <path d="M0.7 -11 C 5.4 -8.6, 6 -4.4, 4.8 -1.6 L 0.7 -1.6 Z" opacity="0.9"/>
      <path d="M-0.7 -7.6 C -5 -5.6, -5.6 -2.6, -4.6 -1.6 L -0.7 -1.6 Z" opacity="0.9"/>
      </g>
      <g transform="translate(82 22) rotate(26)" fill="#04161D" opacity="0.95">
      <path d="M-9 0 L9 0 L6.6 4.8 L-6.6 4.8 Z"/>
      <path d="M-0.6 0 L-0.6 -10 L0.6 -10 L0.6 0 Z"/>
      <path d="M0.6 -9.2 C 4.6 -7.2, 5 -3.6, 4 -1.4 L 0.6 -1.4 Z" opacity="0.9"/>
      </g>

      {/* ── bras d'arrière-plan (silhouettes sombres) ────────────────────── */}
      <path d="M50 58 C 42 48, 32 36, 22 26 C 16 20, 10 16, 5 15 C 12 15, 20 20, 27 27
      C 38 38, 48 50, 55 58 Z" fill={`url(#${uid}-k-arm-deep)`}/>
      <path d="M70 58 C 78 48, 88 37, 98 28 C 104 22, 110 19, 115 18 C 108 18, 100 22, 93 29
      C 82 39, 72 51, 65 58 Z" fill={`url(#${uid}-k-arm-deep)`}/>
      <path d="M56 56 C 52 44, 48 30, 44 18 C 42 12, 40 7, 38 3 C 43 8, 47 16, 50 25
      C 55 38, 59 50, 61 57 Z" fill={`url(#${uid}-k-arm-deep)`} opacity="0.9"/>
      <path d="M64 56 C 68 44, 72 31, 77 20 C 79 14, 82 9, 84 5 C 80 11, 76 19, 73 28
      C 68 40, 63 51, 61 57 Z" fill={`url(#${uid}-k-arm-deep)`} opacity="0.9"/>

      {/* ── les deux longs tentacules, enroulés sur les coques ───────────── */}
      <path d="M54 52 C 50 42, 46 32, 41 26 C 37 26, 33 24, 29 24.5 C 33 20.5, 39 23, 44 29
      C 50 36, 56 44, 59 51 Z" fill={`url(#${uid}-k-arm)`}/>
      <path d="M29 24.5 C 25.6 23, 24 20, 25 17.4 C 22 19, 21 22.8, 23 25.8 C 24.4 27.8, 27 28.8, 29.6 28 Z" fill={`url(#${uid}-k-arm)`}/>
      <g fill="#F0C79F" opacity="0.4">
      <circle cx="56.0" cy="51.4" r="0.67"/><circle cx="57.5" cy="52.8" r="0.55"/><circle cx="52.6" cy="45.0" r="0.67"/><circle cx="54.1" cy="46.4" r="0.55"/><circle cx="48.6" cy="37.6" r="0.64"/><circle cx="50.1" cy="39.0" r="0.52"/>
      <circle cx="44.6" cy="30.6" r="0.61"/><circle cx="46.1" cy="32.0" r="0.5"/><circle cx="39.6" cy="30.6" r="0.58"/><circle cx="41.1" cy="32.0" r="0.48"/><circle cx="34.0" cy="26.6" r="0.55"/><circle cx="35.5" cy="28.0" r="0.45"/>
      </g>

      <path d="M66 52 C 70 42, 74 33, 79 27 C 83 27, 87 24, 91 24.5 C 87 20.5, 81 23, 76 29
      C 70 37, 64 44, 61 51 Z" fill={`url(#${uid}-k-arm)`} opacity="0.96"/>
      <path d="M91 24.5 C 94.4 23, 96 20, 95 17.4 C 98 19, 99 22.8, 97 25.8 C 95.6 27.8, 93 28.8, 90.4 28 Z" fill={`url(#${uid}-k-arm)`} opacity="0.96"/>
      <g fill="#F0C79F" opacity="0.33">
      <circle cx="64.0" cy="51.4" r="0.67"/><circle cx="65.5" cy="52.8" r="0.55"/><circle cx="67.4" cy="45.4" r="0.67"/><circle cx="68.9" cy="46.8" r="0.55"/><circle cx="71.4" cy="38.4" r="0.64"/><circle cx="72.9" cy="39.8" r="0.52"/>
      <circle cx="75.4" cy="31.6" r="0.61"/><circle cx="76.9" cy="33.0" r="0.5"/><circle cx="80.4" cy="31.6" r="0.58"/><circle cx="81.9" cy="33.0" r="0.48"/><circle cx="86.0" cy="27.4" r="0.55"/><circle cx="87.5" cy="28.8" r="0.45"/>
      </g>

      {/* ── le corps : manteau tombant, palmes, tête ─────────────────────── */}
      <g>
      {/* palmes */}
      <path d="M51.5 88 C 42 92, 35 99, 31.5 107 C 40 103.4, 47 100, 53 96.4 Z" fill={`url(#${uid}-k-fin)`}/>
      <path d="M68.5 88 C 78 92, 85 99, 88.5 107 C 80 103.4, 73 100, 67 96.4 Z" fill={`url(#${uid}-k-fin)`}/>
      {/* manteau, cône tombant vers le fond */}
      <path d="M46.5 62 C 45.5 78, 49 96, 55 108 C 57.2 112.6, 58.8 115.4, 60 117
      C 61.2 115.4, 62.8 112.6, 65 108 C 71 96, 74.5 78, 73.5 62 Z" fill={`url(#${uid}-k-body)`}/>
      {/* tête */}
      <path d="M60 44.5 C 69 44.5, 75.6 50.4, 76.2 58 C 76.8 65.6, 70 71.2, 60 71.2
      C 50 71.2, 43.2 65.6, 43.8 58 C 44.4 50.4, 51 44.5, 60 44.5 Z" fill={`url(#${uid}-k-body)`}/>
      </g>
      {/* moucheture de peau */}
      <g filter={`url(#${uid}-k-skin)`} opacity="0.75">
      <path d="M46.5 62 C 45.5 78, 49 96, 55 108 C 57.2 112.6, 58.8 115.4, 60 117 C 61.2 115.4, 62.8 112.6, 65 108 C 71 96, 74.5 78, 73.5 62 Z"/>
      <path d="M60 44.5 C 69 44.5, 75.6 50.4, 76.2 58 C 76.8 65.6, 70 71.2, 60 71.2 C 50 71.2, 43.2 65.6, 43.8 58 C 44.4 50.4, 51 44.5, 60 44.5 Z"/>
      </g>
      {/* modelé : lumière haut-gauche, ombre bas-droite */}
      <path d="M48.6 63 C 47.8 76, 50 90, 55 101 C 51.4 90, 50 76, 50.6 63 Z" fill="#D98B55" opacity="0.4" filter={`url(#${uid}-k-soft2)`}/>
      <path d="M71.6 63 C 72.4 77, 70 91, 65 102 C 69.6 91, 70.6 77, 69.6 63 Z" fill="#1A0803" opacity="0.55" filter={`url(#${uid}-k-soft2)`}/>
      <path d="M52 47.6 C 57 45, 65 45.4, 70 48.6 C 64.6 47, 57.4 46.8, 52 48.8 Z" fill="#E0A06C" opacity="0.45" filter={`url(#${uid}-k-soft2)`}/>
      <ellipse cx="60" cy="70" rx="15" ry="4" fill="#1A0803" opacity="0.4" filter={`url(#${uid}-k-soft)`}/>
      <path d="M66 46 C 73 49, 76.6 54, 76.4 61 C 77 74, 72 92, 65 107 C 71.6 90, 74.4 73, 73 61 C 72.4 54, 70 49, 66 46 Z" fill="#160702" opacity="0.5" filter={`url(#${uid}-k-soft2)`}/>
      <path d="M54.6 47.4 C 50 50, 47.4 54, 47.6 60 C 48.6 55, 51 50.6, 55.6 48.6 Z" fill="#F0B27C" opacity="0.4" filter={`url(#${uid}-k-soft2)`}/>
      <path d="M57.4 76 C 58.6 88, 59.2 100, 59.6 110" fill="none" stroke="#E8AE79" strokeWidth="1.1" opacity="0.22" strokeLinecap="round" filter={`url(#${uid}-k-soft2)`}/>

      <path d="M47.4 63 C 46.6 79, 50 97, 56 109" fill="none" stroke="#9FD8E4" strokeWidth="0.9" opacity="0.3" strokeLinecap="round"/>
      <path d="M44.6 50.6 C 47.6 47.4, 53 45.6, 59 45.4" fill="none" stroke="#9FD8E4" strokeWidth="0.9" opacity="0.32" strokeLinecap="round"/>

      {/* ── les yeux ─────────────────────────────────────────────────────── */}
      <circle cx="48.6" cy="58" r="10" fill={`url(#${uid}-k-eyeglow)`}/>
      <circle cx="71.4" cy="58" r="10" fill={`url(#${uid}-k-eyeglow)`}/>
      <ellipse cx="48.6" cy="58" rx="7.4" ry="6.4" fill="#120602"/>
      <ellipse cx="71.4" cy="58" rx="7.4" ry="6.4" fill="#120602"/>
      <ellipse cx="48.6" cy="58" rx="6.3" ry="5.5" fill="#1C0F06"/>
      <ellipse cx="71.4" cy="58" rx="6.3" ry="5.5" fill="#1C0F06"/>
      <ellipse cx="48.6" cy="58" rx="6.3" ry="5.5" fill="none" stroke={`url(#${uid}-k-iris)`} strokeWidth="1.7"/>
      <ellipse cx="71.4" cy="58" rx="6.3" ry="5.5" fill="none" stroke={`url(#${uid}-k-iris)`} strokeWidth="1.7"/>
      <path d="M43.2 58.2 C 45.6 55.8, 51.6 55.8, 54 58.2 C 51.6 60.8, 45.6 60.8, 43.2 58.2 Z" fill="#050100"/>
      <path d="M66 58.2 C 68.4 55.8, 74.4 55.8, 76.8 58.2 C 74.4 60.8, 68.4 60.8, 66 58.2 Z" fill="#050100"/>
      <ellipse cx="46.2" cy="55.8" rx="1.3" ry="0.85" fill="#FFFBEE" opacity="0.95" transform="rotate(-18 46.2 55.8)"/>
      <ellipse cx="69" cy="55.8" rx="1.3" ry="0.85" fill="#FFFBEE" opacity="0.95" transform="rotate(-18 69 55.8)"/>
      <ellipse cx="50.8" cy="60.6" rx="1.9" ry="0.7" fill="#FFE6A8" opacity="0.3"/>
      <ellipse cx="73.6" cy="60.6" rx="1.9" ry="0.7" fill="#FFE6A8" opacity="0.3"/>
      <path d="M41.6 51.6 C 46.4 51.4, 52.6 53.4, 55.6 56.2 L 55 58 C 51.6 54.6, 46.2 52.6, 41.4 53 Z" fill="#25100A" opacity="0.92"/>
      <path d="M78.4 51.6 C 73.6 51.4, 67.4 53.4, 64.4 56.2 L 65 58 C 68.4 54.6, 73.8 52.6, 78.6 53 Z" fill="#25100A" opacity="0.92"/>
      <path d="M44 63.6 C 47.6 65.2, 51.6 65.2, 54.4 64" fill="none" stroke="#1A0803" strokeWidth="1" opacity="0.55" strokeLinecap="round"/>
      <path d="M76 63.6 C 72.4 65.2, 68.4 65.2, 65.6 64" fill="none" stroke="#1A0803" strokeWidth="1" opacity="0.55" strokeLinecap="round"/>
      <path d="M76 63.6 C 72.4 65.2, 68.4 65.2, 65.6 64" fill="none" stroke="#1A0803" strokeWidth="1" opacity="0.55" strokeLinecap="round"/>

      {/* ── couronne de bras au premier plan ─────────────────────────────── */}
      <path d="M48 66 C 40 70, 30 76, 22 86 C 16 93, 13 100, 13 106 C 8 99, 11 88, 19 79
      C 28 69, 40 63, 50 61 Z" fill={`url(#${uid}-k-arm)`}/>
      <g fill="#F0C79F" opacity="0.4">
      <circle cx="46.0" cy="64.0" r="0.75"/><circle cx="47.5" cy="65.4" r="0.61"/><circle cx="38.6" cy="68.0" r="0.75"/><circle cx="40.1" cy="69.4" r="0.61"/><circle cx="31.0" cy="73.4" r="0.72"/><circle cx="32.5" cy="74.8" r="0.59"/>
      <circle cx="24.6" cy="80.4" r="0.7"/><circle cx="26.1" cy="81.8" r="0.57"/><circle cx="19.6" cy="88.4" r="0.67"/><circle cx="21.1" cy="89.8" r="0.55"/><circle cx="16.4" cy="97.0" r="0.64"/><circle cx="17.9" cy="98.4" r="0.52"/>
      </g>
      <path d="M72 66 C 80 70, 90 76, 98 86 C 104 93, 107 100, 107 106 C 112 99, 109 88, 101 79
      C 92 69, 80 63, 70 61 Z" fill={`url(#${uid}-k-arm)`} opacity="0.96"/>
      <g fill="#F0C79F" opacity="0.34">
      <circle cx="74.0" cy="64.0" r="0.75"/><circle cx="75.5" cy="65.4" r="0.61"/><circle cx="81.4" cy="68.0" r="0.75"/><circle cx="82.9" cy="69.4" r="0.61"/><circle cx="89.0" cy="73.4" r="0.72"/><circle cx="90.5" cy="74.8" r="0.59"/>
      <circle cx="95.4" cy="80.4" r="0.7"/><circle cx="96.9" cy="81.8" r="0.57"/><circle cx="100.4" cy="88.4" r="0.67"/><circle cx="101.9" cy="89.8" r="0.55"/><circle cx="103.6" cy="97.0" r="0.64"/><circle cx="105.1" cy="98.4" r="0.52"/>
      </g>
      <path d="M54 70 C 50 80, 44 90, 36 98 C 31 103, 26 106, 22 107 C 28 111, 37 107, 44 99
      C 52 90, 57 79, 58 70 Z" fill={`url(#${uid}-k-arm-deep)`}/>
      <path d="M66 70 C 70 80, 76 90, 84 98 C 89 103, 94 106, 98 107 C 92 111, 83 107, 76 99
      C 68 90, 63 79, 62 70 Z" fill={`url(#${uid}-k-arm-deep)`} opacity="0.94"/>

      {/* particules en suspension */}
      <g fill="#CFEDF3" opacity="0.28">
      <circle cx="24" cy="34" r="0.5"/><circle cx="88" cy="42" r="0.45"/><circle cx="34" cy="52" r="0.4"/>
      <circle cx="96" cy="62" r="0.5"/><circle cx="18" cy="66" r="0.45"/><circle cx="76" cy="34" r="0.4"/>
      <circle cx="52" cy="28" r="0.4"/><circle cx="66" cy="76" r="0.45"/><circle cx="40" cy="86" r="0.4"/>
      <circle cx="86" cy="88" r="0.42"/><circle cx="28" cy="96" r="0.38"/><circle cx="102" cy="46" r="0.4"/>
      </g>

      <rect x="7" y="66" width="106" height="50" fill={`url(#${uid}-k-vign)`}/>
      </g>

      <circle cx="60" cy="60" r="55.4" fill="none" stroke={`url(#${uid}-k-ring)`} strokeWidth="3.6"/>
      <circle cx="60" cy="60" r="55.4" fill="none" stroke="#3A1605" strokeWidth="3.6" strokeDasharray="3.2 4.6" opacity="0.5"/>
      <circle cx="60" cy="60" r="53.2" fill="none" stroke="#010A0E" strokeWidth="0.8" opacity="0.7"/>
    </>
  )
}

/** Un amiral proue en avant, voiles en lambeaux et fanaux verts, deux escortes dans la brume. */
function GhostArmada({ uid }: { uid: string }) {
  return (
    <>
      <defs>
      <radialGradient id={`${uid}-a-sky`} cx="0.5" cy="0.62" r="0.9">
      <stop offset="0" stopColor="#1D6F76"/><stop offset="0.34" stopColor="#0D4450"/>
      <stop offset="0.72" stopColor="#06222C"/><stop offset="1" stopColor="#010A0E"/>
      </radialGradient>
      <linearGradient id={`${uid}-a-sea`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#0A3B45"/><stop offset="1" stopColor="#020F15"/>
      </linearGradient>
      <linearGradient id={`${uid}-a-fog`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#B6E9E2" stopOpacity="0"/><stop offset="0.5" stopColor="#B6E9E2" stopOpacity="0.2"/><stop offset="1" stopColor="#B6E9E2" stopOpacity="0"/>
      </linearGradient>
      <linearGradient id={`${uid}-a-hull`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#123C44"/><stop offset="1" stopColor="#04161C"/>
      </linearGradient>
      <linearGradient id={`${uid}-a-sail`} x1="0.2" y1="0" x2="0.8" y2="1">
      <stop offset="0" stopColor="#DAF6F0" stopOpacity="0.8"/><stop offset="0.6" stopColor="#8FCFC9" stopOpacity="0.42"/><stop offset="1" stopColor="#4E8F92" stopOpacity="0.14"/>
      </linearGradient>
      <linearGradient id={`${uid}-a-sail-far`} x1="0.2" y1="0" x2="0.8" y2="1">
      <stop offset="0" stopColor="#A9DED8" stopOpacity="0.4"/><stop offset="1" stopColor="#4E8F92" stopOpacity="0.08"/>
      </linearGradient>
      <radialGradient id={`${uid}-a-lamp`} cx="0.5" cy="0.5" r="0.5">
      <stop offset="0" stopColor="#C9FFE6" stopOpacity="0.95"/><stop offset="0.35" stopColor="#6FE9BC" stopOpacity="0.5"/><stop offset="1" stopColor="#6FE9BC" stopOpacity="0"/>
      </radialGradient>
      <linearGradient id={`${uid}-a-vign`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#010A0E" stopOpacity="0"/><stop offset="1" stopColor="#010A0E" stopOpacity="0.8"/>
      </linearGradient>
      <linearGradient id={`${uid}-a-ring`} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor="#F0DCA8"/><stop offset="0.55" stopColor="#A5813F"/><stop offset="1" stopColor="#5A2A12"/>
      </linearGradient>
      <filter id={`${uid}-a-soft`} x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="1.7"/></filter>
      <filter id={`${uid}-a-soft2`} x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="0.6"/></filter>
      <filter id={`${uid}-a-grain`} x="-20%" y="-20%" width="140%" height="140%">
      <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="3" seed="11" result="n"/>
      <feColorMatrix in="n" type="matrix"
      values="0 0 0 0 0.74  0 0 0 0 0.94  0 0 0 0 0.90  0 0 0 -1.05 0.52" result="m"/>
      <feComposite in="m" in2="SourceGraphic" operator="in"/>
      </filter>
      <clipPath id={`${uid}-a-clip`}><circle cx="60" cy="60" r="53"/></clipPath>
      </defs>

      <g clipPath={`url(#${uid}-a-clip)`}>
      <rect x="7" y="7" width="106" height="106" fill={`url(#${uid}-a-sky)`}/>

      {/* halo blafard derrière la flotte */}
      <ellipse cx="60" cy="74" rx="40" ry="26" fill="#5FD9B8" opacity="0.12" filter={`url(#${uid}-a-soft)`}/>

      {/* ── rangs du fond, à peine devinés ───────────────────────────────── */}
      <g opacity="0.42">
      <g transform="translate(24 78) scale(0.62)">
      <path d="M-14 0 L14 0 L10 8 L-10 8 Z" fill={`url(#${uid}-a-hull)`}/>
      <path d="M-0.9 0 L-0.9 -30 L0.9 -30 L0.9 0 Z" fill="#0E323A"/>
      <path d="M0.9 -28 C 10 -23, 12 -11, 9 -2 L 0.9 -2 Z" fill={`url(#${uid}-a-sail-far)`}/>
      <path d="M-0.9 -20 C -9 -16, -10 -7, -8 -2 L -0.9 -2 Z" fill={`url(#${uid}-a-sail-far)`}/>
      </g>
      <g transform="translate(97 76) scale(0.55)">
      <path d="M-14 0 L14 0 L10 8 L-10 8 Z" fill={`url(#${uid}-a-hull)`}/>
      <path d="M-0.9 0 L-0.9 -28 L0.9 -28 L0.9 0 Z" fill="#0E323A"/>
      <path d="M-0.9 -26 C -10 -21, -12 -10, -9 -2 L -0.9 -2 Z" fill={`url(#${uid}-a-sail-far)`}/>
      </g>
      </g>

      {/* brume basse derrière l'amiral */}
      <ellipse cx="60" cy="80" rx="58" ry="9" fill={`url(#${uid}-a-fog)`} opacity="0.85"/>

      {/* ── escorte gauche ───────────────────────────────────────────────── */}
      <g transform="translate(31 86) scale(0.8) rotate(-6)" opacity="0.9">
      <path d="M-17 0 C -12 6, 12 6, 17 0 L 13 9 L -13 9 Z" fill={`url(#${uid}-a-hull)`} stroke="#7FD6C6" strokeWidth="0.5" strokeOpacity="0.35"/>
      <path d="M-1 0 L-1 -34 L1 -34 L1 0 Z" fill="#0E323A"/>
      <path d="M1 -32 C 12 -26, 14 -12, 10.4 -2 L 1 -2 Z" fill={`url(#${uid}-a-sail)`}/>
      <path d="M-1 -22 C -11 -17, -12.4 -7, -10 -2 L -1 -2 Z" fill={`url(#${uid}-a-sail-far)`}/>
      <path d="M10.4 -2 L 8.6 3 M5 -2 L 3.6 4.4 M1.4 -2 L 0.8 2.6" stroke="#DAF6F0" strokeWidth="0.6" opacity="0.4"/>
      <circle cx="0" cy="-3" r="5" fill={`url(#${uid}-a-lamp)`} opacity="0.7"/>
      </g>

      {/* ── escorte droite ───────────────────────────────────────────────── */}
      <g transform="translate(90 84) scale(0.74) rotate(7)" opacity="0.86">
      <path d="M-17 0 C -12 6, 12 6, 17 0 L 13 9 L -13 9 Z" fill={`url(#${uid}-a-hull)`} stroke="#7FD6C6" strokeWidth="0.5" strokeOpacity="0.3"/>
      <path d="M-1 0 L-1 -32 L1 -32 L1 0 Z" fill="#0E323A"/>
      <path d="M-1 -30 C -12 -24, -14 -11, -10.4 -2 L -1 -2 Z" fill={`url(#${uid}-a-sail)`}/>
      <path d="M1 -20 C 11 -15, 12.4 -6, 10 -2 L 1 -2 Z" fill={`url(#${uid}-a-sail-far)`}/>
      <circle cx="0" cy="-3" r="5" fill={`url(#${uid}-a-lamp)`} opacity="0.6"/>
      </g>

      {/* ── L'AMIRAL, au centre, proue vers nous ─────────────────────────── */}
      <g>
      {/* mâture */}
      <path d="M59 76 L59 22 L61 22 L61 76 Z" fill="#10353D"/>
      <path d="M44 34 L76 34 L76 35.4 L44 35.4 Z" fill="#10353D"/>
      <path d="M48 50 L72 50 L72 51.4 L48 51.4 Z" fill="#10353D"/>
      <g stroke="#9FE8DA" strokeWidth="0.4" opacity="0.3" fill="none">
      <path d="M60 24 L44 35 M60 24 L76 35 M60 34 L48 50.6 M60 34 L72 50.6 M60 50 L41 77 M60 50 L79 77"/>
      </g>
      {/* grand-voile déchirée */}
      <path d="M61 38 C 75 42, 78.6 56, 74 66 L 70 62.4 L 66.6 67 L 63 62.8 L 61 66.6 Z" fill={`url(#${uid}-a-sail)`}/>
      <path d="M59 38 C 45 42, 41.4 56, 46 66 L 50 62.4 L 53.4 67 L 57 62.8 L 59 66.6 Z" fill={`url(#${uid}-a-sail)`} opacity="0.8"/>
      <path d="M75 68 C 73 73.4, 71.4 76, 70 78 L 68.6 70.6 Z" fill={`url(#${uid}-a-sail)`} opacity="0.6"/>
      <path d="M65 68 C 64.4 74.4, 63.6 77.4, 62.6 80 L 61.6 69.4 Z" fill={`url(#${uid}-a-sail)`} opacity="0.55"/>
      <path d="M52 68 C 51.4 74, 50.4 77, 49.4 79.4 L 48.4 69 Z" fill={`url(#${uid}-a-sail)`} opacity="0.5"/>
      <path d="M45 68 C 43.6 72.6, 42.4 75, 41 77 L 40.4 70 Z" fill={`url(#${uid}-a-sail)`} opacity="0.45"/>
      {/* hunier */}
      <path d="M61 24 C 67.4 26.4, 68.6 30.4, 66.8 33.4 L 61 33.4 Z" fill={`url(#${uid}-a-sail)`} opacity="0.62"/>
      <path d="M59 24 C 52.6 26.4, 51.4 30.4, 53.2 33.4 L 59 33.4 Z" fill={`url(#${uid}-a-sail)`} opacity="0.55"/>
      {/* pavillon en lambeaux */}
      <path d="M61 22 L72 25 L61 28 Z" fill="#8FF0CE" opacity="0.65"/>
      {/* coque, vue de trois quarts avant */}
      <path d="M38 76 C 46 84, 74 84, 82 76 L 76 94 C 68 98.6, 52 98.6, 44 94 Z" fill={`url(#${uid}-a-hull)`}/>
      <path d="M38 76 C 46 84, 74 84, 82 76 L 81 79.4 C 73 87, 47 87, 39 79.4 Z" fill="#1B535A"/>
      <path d="M38 76 C 46 84, 74 84, 82 76" fill="none" stroke="#9FE8DA" strokeWidth="0.7" opacity="0.5"/>
      {/* sabords, lanternes vertes */}
      <g fill="#8FF0CE">
      <circle cx="48" cy="86.6" r="1.5" opacity="0.85"/><circle cx="55" cy="88.4" r="1.5" opacity="0.9"/>
      <circle cx="65" cy="88.4" r="1.5" opacity="0.9"/><circle cx="72" cy="86.6" r="1.5" opacity="0.85"/>
      </g>
      <circle cx="60" cy="74" r="9" fill={`url(#${uid}-a-lamp)`}/>
      <circle cx="60" cy="74" r="1.9" fill="#E8FFF6"/>
      {/* figure de proue squelettique */}
      <path d="M60 96 C 58.6 99.4, 58.6 102.4, 60 104.6 C 61.4 102.4, 61.4 99.4, 60 96 Z" fill="#9FE8DA" opacity="0.55"/>
      {/* bordé qui laisse passer la lumière : le vaisseau est translucide */}
      <path d="M44 94 C 52 98.6, 68 98.6, 76 94 L 74.6 98 C 67 102, 53 102, 45.4 98 Z" fill="#6FE9BC" opacity="0.16"/>
      </g>

      {/* grain spectral sur les voiles */}
      <g filter={`url(#${uid}-a-grain)`} opacity="0.5">
      <path d="M61 38 C 75 42, 78.6 56, 74 66 L 70 62.4 L 66.6 67 L 63 62.8 L 61 66.6 Z"/>
      <path d="M59 38 C 45 42, 41.4 56, 46 66 L 50 62.4 L 53.4 67 L 57 62.8 L 59 66.6 Z"/>
      </g>

      {/* ── la mer ───────────────────────────────────────────────────────── */}
      <path d="M7 96 C 22 92.6, 34 99, 48 96 C 62 93, 74 99.4, 88 96.4 C 98 94.4, 106 96, 113 98 L 113 116 L 7 116 Z" fill={`url(#${uid}-a-sea)`}/>
      <path d="M7 96 C 22 92.6, 34 99, 48 96 C 62 93, 74 99.4, 88 96.4 C 98 94.4, 106 96, 113 98" fill="none" stroke="#8FF0CE" strokeWidth="0.7" opacity="0.32"/>
      {/* reflets verts sur l'eau */}
      <g fill="#8FF0CE" opacity="0.22" filter={`url(#${uid}-a-soft2)`}>
      <ellipse cx="60" cy="101" rx="13" ry="1.5"/><ellipse cx="60" cy="105" rx="8" ry="1.1"/><ellipse cx="60" cy="108.6" rx="4.6" ry="0.9"/>
      <ellipse cx="31" cy="100" rx="7" ry="1"/><ellipse cx="90" cy="99" rx="6" ry="0.9"/>
      </g>

      {/* nappes de brume par-dessus tout */}
      <ellipse cx="52" cy="70" rx="54" ry="7" fill={`url(#${uid}-a-fog)`} opacity="0.7"/>
      <ellipse cx="70" cy="56" rx="46" ry="6" fill={`url(#${uid}-a-fog)`} opacity="0.55"/>
      <ellipse cx="60" cy="90" rx="58" ry="8" fill={`url(#${uid}-a-fog)`} opacity="0.6"/>
      <ellipse cx="40" cy="40" rx="40" ry="5" fill={`url(#${uid}-a-fog)`} opacity="0.35"/>

      <rect x="7" y="70" width="106" height="46" fill={`url(#${uid}-a-vign)`}/>
      </g>

      <circle cx="60" cy="60" r="55.4" fill="none" stroke={`url(#${uid}-a-ring)`} strokeWidth="3.6"/>
      <circle cx="60" cy="60" r="55.4" fill="none" stroke="#3A1605" strokeWidth="3.6" strokeDasharray="3.2 4.6" opacity="0.5"/>
      <circle cx="60" cy="60" r="53.2" fill="none" stroke="#010A0E" strokeWidth="0.8" opacity="0.7"/>
    </>
  )
}
